import { MadeByLong } from "./components/MadeBy";
import { Header } from "./components/Header";
import { Dropdowns } from "./components/Dropdowns";
import { usePartsFinder } from "./hooks/usePartsFinder";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { PartsList } from "./components/PartsList";
import { SymbolsExpalanation } from "./components/SymbolsExplanation";
import { FullScreenImageModal } from "./components/FullScreenImageModal";
import { useHoverParts } from "./hooks/useHoverParts";
import { PartsImage } from "./components/PartsImage";
import { RequestQuoteModal } from "./components/RequestQuoteModal";
import { useRequestQuote } from "./hooks/useRequestQuote";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import translationEN from "./locales/en/translations.json";
import translationRO from "./locales/ro/translations.json";
import { useEffect, useRef, useState } from "react";
import { Overlay } from "./components/Overlay";

type PropsType = {
  permanentDomain: string;
  country: string;
  forceBrand?: string;
};

const resources: Record<string, { translation: Record<string, string> }> = {
  en: {
    translation: translationEN,
  },
  ro: {
    translation: translationRO,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: "ro", // default RO
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const PartsFinderWidget = ({ permanentDomain, country, forceBrand }: PropsType) => {
  const {
    theme,
    shopAutoCreateProduct,
    shopShowExternalStock,
    models,
    diagrams,
    parts,
    loadingParts,
    loadedInitialData,
    hasMonthlySubscription,
    showImagesInPartsFinder,
    imageModalUrl,
    ownerManualLink,
    onRequestCartMessage,

    selectedBrand,
    selectedYear,
    selectedTypeName,
    selectedModelId,
    selectedDiagram,

    overlay,

    onBrandSelected,
    onTypeSelected,
    onYearSelected,
    onModelSelected,
    onDiagramSelected,
    onAddToCart,
    openVariant,
    openImageModal,
    closeImageModal,
  } = usePartsFinder({
    permanentDomain,
    country,
    forceBrand,
  });

  const {
    requestQuoteForPart,
    setRequestQuoteForPart,
    close: closeRequestQuote,
    onSend: onSendRequestQuote,
    saving: isSendingRequestQuote,
  } = useRequestQuote();

  const { hoveredPart, onHoverPartItem } = useHoverParts();
  const imageRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [imageTop, setImageTop] = useState(0);

  // Update language when countryCode is available
  useEffect(() => {
    if (country) {
      const language = country.toLowerCase();

      if (resources[language]) {
        i18n.changeLanguage(language);
      } else {
        // Fallback to default language if country code is not supported
        i18n.changeLanguage(i18n.options.fallbackLng as string);
      }
    } else {
      // Fallback to default language if country code is not supported
      i18n.changeLanguage(i18n.options.fallbackLng as string);
    }
  }, [country]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth <= 768) {
        setIsFixed(true);
        setImageTop(0);
        return; // Don't handle scroll events on mobile
      }
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const parentRect = imageRef.current.parentElement?.getBoundingClientRect();

        if (!parentRect) return;

        const originalTop = parentRect.top;
        const extraPaddingTop = 100;

        if (originalTop < extraPaddingTop) {
          const maxScroll = parentRect.height - rect.height;
          const newTop = Math.min(Math.max(0, -originalTop + extraPaddingTop), maxScroll);
          setImageTop(newTop);
          setIsFixed(true);
        } else {
          setIsFixed(false);
          setImageTop(0);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const partsVisible = parts && Object.keys(parts)?.length > 0;

  if (!theme || !loadedInitialData) {
    return (
      <div className="flex items-center justify-center h-svh">
        <div className="text-2xl">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  return (
    <main className="w-full flex flex-col items-center justify-between" style={{ backgroundColor: theme.partsFinder.bgColor }}>
      {overlay && <Overlay message={overlay.message} type={overlay.type} />}
      <div className="w-full items-start justify-between flex overflow-y-auto overflow-x-auto" style={{ maxWidth: 1366 }}>
        <div className="w-full pt-1">
          <Header brand={selectedBrand} />

          <Dropdowns
            forcedBrand={forceBrand}
            initialBrand={selectedBrand}
            initialYear={selectedYear}
            initialTypeName={selectedTypeName}
            initialModelId={selectedModelId}
            initialDiagramId={selectedDiagram?.id}
            initialData={loadedInitialData}
            ownerManualLink={ownerManualLink}
            models={models || {}}
            diagramsByModel={diagrams || {}}
            onBrandSelected={(brand) => onBrandSelected(brand)}
            onYearSelected={onYearSelected}
            onTypeSelected={onTypeSelected}
            onModelSelected={onModelSelected}
            onDiagramSelected={onDiagramSelected}
          />

          <div
            className="w-full grow flex mt-6 flex-col-reverse md:flex-row justify-center items-start mb-3"
            style={{ backgroundColor: theme.partsFinder.bgColor }}
          >
            <div className="w-full lg:w-1/2 px-[3px]">
              {loadingParts ? (
                <div className="flex items-center justify-center h-96">
                  <LoadingSpinner size="medium" />
                </div>
              ) : (
                parts && (
                  <PartsList
                    showProductImages={showImagesInPartsFinder}
                    hasMonthlySubscription={hasMonthlySubscription}
                    parts={parts || []}
                    onAddToCart={onAddToCart}
                    onClickTitle={openVariant}
                    onClickImage={openImageModal}
                    onHoverPartItem={onHoverPartItem}
                    onClickRequestQuote={setRequestQuoteForPart}
                    shopAutoCreateProduct={shopAutoCreateProduct}
                    shopShowExternalStock={shopShowExternalStock}
                  />
                )
              )}
            </div>

            <div
              ref={imageRef}
              className="flex grow items-center w-full lg:w-1/2"
              style={{
                position: isFixed ? "relative" : "static",
                transform: isFixed ? `translateY(${imageTop}px)` : "none",
                transition: "transform 0.05s ease-out",
              }}
            >
              {selectedDiagram && (
                <PartsImage
                  className="flex grow"
                  src={`https://partsfinder.clpstudio.ro/diagrams/${selectedBrand}/${selectedDiagram.file_name}`}
                  circleCssPoints={hoveredPart?.points}
                  no={hoveredPart?.no}
                />
              )}
            </div>
          </div>

          <SymbolsExpalanation selectedDiagramId={selectedDiagram?.id} onRequestCartMessage={onRequestCartMessage} />

          <div style={{ marginTop: partsVisible ? 24 : 200, marginBottom: 24 }}>
            <MadeByLong />
          </div>

          {imageModalUrl && <FullScreenImageModal src={imageModalUrl} alt="Image" onClose={closeImageModal} />}

          {requestQuoteForPart && (
            <RequestQuoteModal
              partName={requestQuoteForPart.name}
              partSku={requestQuoteForPart.sku}
              onClose={closeRequestQuote}
              onSend={onSendRequestQuote}
              isSendingRequestQuote={isSendingRequestQuote}
              shop={permanentDomain}
            />
          )}
        </div>
      </div>
    </main>
  );
};

export default PartsFinderWidget;
